/** ALP-83 (rules without fixed options): tag / category pickers, agent actions, rule tag stats. */
const E1_EN = {
  tagPicker: {
    label: "Tags and AI categories",
    add: "Add",
    search: "Search or create…",
    builtIn: "Built-in tags",
    custom: "Your tags",
    categories: "AI categories",
    noCustom: "No tags yet",
    noCategories: "No AI categories yet",
    noMatch: "Nothing matches",
    create: "Create “{{label}}”",
    createTag: "Create tag",
    createCategory: "Create AI category",
    newTitle: "New tag",
    newCategoryTitle: "New AI category",
    name: "Name",
    namePlaceholder: "e.g. Positive",
    color: "Colour",
    isCategory: "AI category",
    isCategoryHint: "The AI puts new comments in this category (with a confidence) using the description and examples.",
    description: "What it means",
    descriptionPlaceholder: "e.g. the customer wants their money back",
    examples: "Examples",
    examplesPlaceholder: "Type an example and press Enter",
    save: "Create",
    cancel: "Cancel",
    created: "“{{label}}” created and selected",
    failed: "Couldn't create it",
    remove: "Remove {{label}}",
    manage: "Manage tags",
    categoryBadge: "AI",
  },
  agentPicker: {
    label: "Agent",
    placeholder: "Choose an agent",
    none: "No agents yet",
    create: "Create an agent",
    unavailable: "Agents couldn't be loaded",
    paused: "Paused",
    modeDraft: "Drafts replies for your approval",
    modeAuto: "Sends replies automatically",
    private: "Reply privately (one DM per comment)",
    privateHint: "Instagram allows one private reply per comment, within 7 days.",
    dmHint: "Sent only inside Instagram's 24-hour messaging window.",
    legacy: "Legacy",
    legacyHint: "Replies with the agent assigned to the conversation or account. Pick a specific agent with “Reply with an AI agent”.",
    errAgent: "Choose an agent.",
  },
  conditionsExtra: {
    minSentimentConfidence: "Only when the AI is at least this sure",
    anyConfidence: "Any confidence",
    categoryHint: "Need your own categories (e.g. positive, wants a refund)?",
    createCategory: "Create an AI category",
    categoryConfidence: "Minimum confidence",
    errTags: "Choose at least one tag.",
    errCategories: "Choose at least one AI category.",
  },
  tagStats: {
    title: "What this rule tagged",
    summary: "This rule tagged {{week}} comments this week · {{all}} all time",
    week: "This week",
    allTime: "All time",
    byTag: "By tag",
    posts: "Across these posts",
    none: "Nothing tagged yet. Matches will show up here.",
    openInbox: "Open in inbox",
    openPost: "View tagged comments on this post",
    count_one: "{{count}} comment",
    count_other: "{{count}} comments",
    cardBadge_one: "{{count}} tagged",
    cardBadge_other: "{{count}} tagged",
  },
};

/** ALP-84 (slice P3): calmer rules list, builder sections, automation log in words. */
const CALM_EN = {
  rulesTitle: "Rules",
  rulesDescription: "Rules answer, hide or tag comments and messages for you: automatically, after your approval, or as suggestions.",
  logLink: "Automation log",
  newMenu: {
    describe: "Describe it",
    describeHint: "Write what should happen; AI drafts the rule.",
    template: "Start from a template",
    templateHint: "Proven rules for Instagram, ready to edit.",
    blank: "Blank rule",
    blankHint: "Build it step by step.",
  },
  card: {
    sentence: "When {{trigger}} and {{condition}}: {{actions}}.",
    ran_zero: "Hasn't run this week",
    ran_one: "Ran once this week",
    ran_other: "Ran {{count}} times this week",
    waiting_one: "{{count}} waiting for approval",
    waiting_other: "{{count}} waiting for approval",
    on: "On",
    off: "Off",
    openLog: "Open log",
    turnOn: "Turn on",
    turnOff: "Turn off",
  },
  firstUse: "Rules answer, hide or tag comments for you. Pick a template to start, or describe your own.",
  steps: {
    when: "When",
    whenHint: "What starts the rule, and where.",
    if: "If",
    ifHint: "Leave empty to act on everything.",
    then: "Then",
    thenHint: "What Malfak does, in order.",
    how: "How",
    howHint: "Act right away, ask first, or only suggest.",
  },
  advanced: "Advanced",
  advancedHint: "Priority, hourly limit and quiet hours",
  moreConditions: "More conditions",
  nameAndLook: "Name and look",
  more: "More",
  viewJson: "View as JSON",
  hideJson: "Hide JSON",
  testTitle: "Test this rule",
  testHint: "Try the rule on your latest comments before it goes live. Nothing is sent.",
  testCommentsOnly: "Tests on recent comments only.",
  fixList: "Fix these before saving",
  goToField: "Go to field",
  errorName: "Name: give the rule a name.",
  errorConditions: "If: some conditions are incomplete.",
  errorActions: "Then: one action needs a detail.",
  errorActionsEmpty: "Then: add at least one action.",
  errorOther: "Something in the rule isn't valid yet.",
  notifyInApp: "Notifies in the app",
  unavailable: {
    comingSoon: "Coming soon",
    notifyChannels: "Only in-app notifications are sent today; email, Slack and Telegram are coming soon.",
  },
  log: {
    title: "Automation log",
    description: "Everything your rules did, in plain words: what they saw and what happened.",
    whatHappened: "What happened",
    filters: "Filters",
    columns: "Columns",
    sentence: "{{action}} · {{event}}",
    sentenceNoAction: "Looked at a {{event}}",
    commentWord: "comment",
    messageWord: "message",
    noAction: "No action",
    technical: "Technical details",
    eventType: "Event type",
    staffTrace: "Open AI trace (staff)",
  },
};

export const automation = {
  title: "Rules",
  ...CALM_EN,
  description: "Rules that read every new comment and DM, and act in your brand's voice — automatically, after your approval, or as suggestions.",
  newRule: "New rule",
  runsLink: "Automation log",
  list: {
    active: "Active",
    paused: "Paused",
    activeCount: "{{n}} active",
    priority: "Priority {{n}}",
    matched: "Matched",
    executed: "Executed",
    pending: "Pending",
    last7: "last 7 days",
    edit: "Edit rule",
    duplicate: "Duplicate",
    delete: "Delete rule",
    deleted: "Rule deleted",
    toggledOn: "“{{name}}” is now active",
    toggledOff: "“{{name}}” is paused",
    when: "When",
    if: "If",
    then: "Then",
    emptyTitle: "No rules yet",
    emptyHint: "Start from a template or describe what you want in plain words. Every rule can be tested on recent comments before it goes live.",
    allAccounts: "All accounts",
    onePost: "One post",
    filterAll: "All",
    filterActive: "Active",
    filterPaused: "Paused",
    searchPlaceholder: "Search rules…",
    noMatch: "No rules match this filter",
  },
  prompt: {
    title: "Describe a rule, AI drafts it",
    hint: "For example: “When someone asks about the price, reply that we sent details and DM them the price list.”",
    placeholder: "When someone…",
    submit: "Draft rule",
    drafting: "Drafting…",
    examples: "Try:",
    example1: "Hide spam and comments with links",
    example2: "When people ask the price, DM them",
    example3: "Angry comments wait for my approval",
    example4: "If someone comments “GUIDE”, send the guide",
    tooShort: "Describe the rule in a few more words.",
  },
  templates: {
    title: "Start from a template",
    hint: "Proven automations for Instagram. You can edit everything before saving.",
    use: "Use template",
  },
  triggers: {
    comment_created: "New comment",
    message_received: "New DM",
    mention: "Mention",
    story_reply: "Story reply",
    postback: "Button tap",
  },
  modes: {
    auto: "Automatic",
    approve: "Approve first",
    suggest: "Suggest only",
    autoHint: "Malfak acts right away. Best for spam and simple, safe replies.",
    approveHint: "Malfak drafts the action and waits in the approval queue for a person.",
    suggestHint: "Malfak only suggests a reply in the inbox; nothing is sent.",
  },
  ops: {
    all: "All of",
    any: "Any of",
    not: "Not",
    keyword: "Keyword",
    regex: "Text pattern (advanced)",
    language: "Language",
    intent: "AI intent",
    sentiment: "Sentiment",
    ai_prompt: "AI judges a description",
    first_time_contact: "First-time contact",
    is_reply: "Is a reply",
    has_link: "Contains a link",
    commenter_marked: "Commenter is marked",
    has_tag: "Has a tag",
    custom_category: "AI category",
    commenter_blocked: "Commenter is blocked in Malfak",
  },
  opHints: {
    keyword: "Matches if the text contains (or exactly equals) any word.",
    regex: "Advanced: a regular expression, tested case-insensitively.",
    language: "Detected language of the comment or message.",
    intent: "What the person wants, classified by AI with a confidence.",
    sentiment: "The tone of the message.",
    ai_prompt: "Describe the condition in plain words; AI decides. Slower, so it runs last.",
    first_time_contact: "This person has never commented or messaged before.",
    is_reply: "The comment answers another comment.",
    has_link: "The text contains a URL or a domain.",
    commenter_marked: "The person was marked harmful, a known troll or VIP by your team.",
    has_tag: "The item carries one of these tags or AI categories.",
    custom_category: "Your own AI categories (e.g. positive, negative, wants a refund), with a confidence.",
    commenter_blocked: "Blocked people are skipped by every other rule; rules with this condition still run for them, but never reply or use AI.",
  },
  summary: {
    always: "every event",
    keyword: "text contains {{words}}",
    keywordExact: "text is exactly {{words}}",
    regex: "text matches /{{pattern}}/",
    language: "language is {{list}}",
    intent: "intent is {{list}} ({{conf}} or more)",
    sentiment: "sentiment is {{list}}",
    sentimentConf: "sentiment is {{list}} ({{conf}} or more)",
    ai_prompt: "AI: “{{text}}”",
    first_time_contact: "first-time contact",
    is_reply: "it's a reply",
    has_link: "it contains a link",
    commenter_marked: "the commenter is marked {{list}}",
    has_tag: "it has the tag {{list}}",
    custom_category: "AI category is {{list}} ({{conf}} or more)",
    commenter_blocked: "the commenter is blocked in Malfak",
    not: "not ({{inner}})",
    and: " and ",
    or: " or ",
    empty: "nothing yet",
    listOr: " or ",
  },
  actions: {
    reply_public: "Reply publicly",
    reply_private: "Reply privately (DM)",
    send_message: "Send a DM",
    ai_agent_reply: "Reply with the assigned agent",
    hide: "Hide the comment",
    delete: "Delete the comment",
    tag: "Tag the person",
    assign: "Assign to a teammate",
    notify: "Notify",
    flag: "Tag the item",
    mark_commenter: "Mark the commenter",
    enrich_commenter: "Fetch the commenter's profile",
    reply_with_agent: "Reply with an AI agent",
    dm_with_agent: "DM with an AI agent",
    summaryFlag: "tag {{flags}}",
    summaryAgent: "reply with {{agent}}",
    summaryAgentPrivate: "reply privately with {{agent}}",
    summaryAgentDm: "DM with {{agent}}",
    summaryMark: "mark the commenter {{mark}}",
    summaryTag: "tag {{tags}}",
    summaryAssign: "assign to {{name}}",
    summaryNotify: "notify via {{channels}}",
    aiPersonalized: "AI-personalized",
  },
  marks: {
    harmful: "Harmful",
    troll: "Known troll",
    vip: "VIP",
  },
  flagTarget: {
    item: "The comment or conversation",
    contact: "The person",
  },
  notifyChannels: {
    in_app: "In-app",
    email: "Email",
    slack: "Slack",
    telegram: "Telegram",
  },
  builder: {
    newTitle: "New rule",
    editTitle: "Edit rule",
    back: "Rules",
    basics: "Basics",
    name: "Rule name",
    namePlaceholder: "e.g. Price questions → DM",
    icon: "Icon",
    color: "Colour",
    trigger: "When this happens",
    scope: "Where",
    scopeAccount: "Account",
    scopeAllAccounts: "All connected accounts",
    scopePost: "Only on one post",
    scopeAnyPost: "Any post",
    conditions: "If these conditions match",
    conditionsHint: "Cheap checks run first; AI checks run last.",
    addCondition: "Add condition",
    addGroup: "Add group",
    removeCondition: "Remove condition",
    wrapNot: "Negate (NOT)",
    unwrapNot: "Remove NOT",
    groupAll: "ALL must match",
    groupAny: "ANY can match",
    switchToAll: "Switch to ALL",
    switchToAny: "Switch to ANY",
    emptyGroup: "Empty group — add a condition",
    words: "Words",
    wordsPlaceholder: "Type a word and press Enter",
    contains: "Contains",
    exact: "Exact",
    pattern: "Pattern",
    testText: "Test text",
    testMatches: "Matches",
    testNoMatch: "No match",
    invalidRegex: "Invalid pattern",
    minConfidence: "Minimum confidence",
    describe: "Describe it",
    describePlaceholder: "e.g. the person sounds angry or disappointed with delivery",
    noOptions: "This condition has no options.",
    actions: "Then do this",
    actionsHint: "Actions run in order. The action itself is decided by the rule, never by the AI.",
    addAction: "Add action",
    moveUp: "Move up",
    moveDown: "Move down",
    removeAction: "Remove action",
    template: "Message",
    templatePlaceholder: "Write the reply…",
    variables: "Insert:",
    aiPersonalize: "Let AI personalize it",
    aiPersonalizeHint: "AI rewrites the message for each person in your brand voice.",
    agent: "AI agent",
    agentNames: { "dm-agent": "Direct messages AI agent", "sales-agent": "Sales AI agent", "support-agent": "Support AI agent" },
    tags: "Tags",
    tagsPlaceholder: "Add a tag",
    assignee: "Teammate",
    pickAssignee: "Choose a teammate",
    channels: "Channels",
    mode: "How should Malfak act?",
    igRules: "Instagram rules apply: one private reply per comment within 7 days, and DMs only within 24 hours of the person's last message.",
    limits: "Safety limits",
    perHour: "At most {{n}} actions per hour",
    quietHours: "Quiet hours",
    quietHoursHint: "No automatic actions in this window; matches wait until it ends.",
    from: "From",
    to: "To",
    timezone: "Time zone",
    priority: "Priority",
    priorityHint: "Lower numbers run first when several rules match.",
    active: "Active",
    activeHint: "Paused rules can still be simulated.",
    save: "Save rule",
    saving: "Saving…",
    created: "Rule created",
    saved: "Rule saved",
    deleteConfirm: "Delete “{{name}}”? Its automation log stays.",
    unsaved: "You have unsaved changes. Leave anyway?",
    invalid: "Fix the highlighted fields before saving.",
    errName: "Give the rule a name.",
    errActions: "Add at least one action.",
    errConditions: "Some conditions are incomplete.",
    errAssignee: "Choose a teammate.",
    errTags: "Add at least one tag.",
    errTemplate: "Write the message.",
    errChannels: "Pick at least one channel.",
    json: "JSON",
    showJson: "Show JSON",
    hideJson: "Hide JSON",
    fromPrompt: "Drafted by AI from your description. Review it, run a simulation, then save.",
    fromTemplate: "Started from a template. Review it, run a simulation, then save.",
    notFound: "This rule doesn't exist anymore.",
  },
  simulate: {
    title: "Test this rule",
    hint: "A dry run of this (unsaved) rule against your latest comments. Nothing is sent.",
    run: "Run simulation",
    rerun: "Run again",
    running: "Simulating…",
    sample: "Sample size",
    matched: "Matched",
    notMatched: "Not matched",
    matchRate: "{{matched}} of {{total}} would match",
    empty: "Run a simulation to see which comments this rule would catch.",
    noResults: "No recent comments in this scope.",
    showAll: "All",
    showMatched: "Matched",
    showUnmatched: "Not matched",
    stale: "The rule changed since the last run.",
    reason: "Why",
  },
  runs: {
    title: "Automation log",
    description: "Everything your rules did, in plain words: what they saw and what happened.",
    rule: "Rule",
    allRules: "All rules",
    result: "Result",
    allResults: "All results",
    event: "Event",
    labels: "AI categories",
    action: "Action",
    when: "When",
    trace: "Trace",
    openTrace: "Open trace",
    noTrace: "No trace",
    matched: "Matched",
    notMatched: "Did not match",
    details: "Details",
    hideDetails: "Hide details",
    loadMore: "Load more",
    emptyTitle: "No runs yet",
    emptyHint: "Runs appear here as soon as a rule evaluates a new comment or message.",
    confidence: "Confidence",
    language: "Language",
    toxicity: "Toxicity",
    eventComment: "Comment",
    eventMessage: "Message",
    deleted: "Deleted rule",
    results: {
      executed: "Done",
      pending_approval: "Waiting approval",
      skipped: "Skipped",
      failed: "Failed",
      suggested: "Suggested",
    },
  },
  ...E1_EN,
};
